import { GmrFunction } from '../core/gmr.models';

//
// What a search over the report catalogue looks at. The principle is "you can find what you can
// read": every entry here is text the user actually sees on screen.
//
// These three hold RESOURCE KEYS, and the screen renders them through @ngx-translate
// (`[translate]="fn.cfn_description"` and friends), so the key is translated here too rather than
// searched literally.
//
const TRANSLATED_KEY_FIELDS = ['cfn_description', 'cfn_documentation', 'can_description'] as const;

//
// Searched as-is. Not visible in the list, but people who know a report's technical name type it -
// and it is the identity a WebMCP agent works with, since preset names are what list_reports
// returns and describe_report/open_report/prepare_report take back in.
//
const RAW_FIELDS = ['cfn_preset_name'] as const;

//
// History, because two rewrites have passed through here and the reasons matter:
//
// Until 2026-08-13 this was AngularJS' built-in `filter:functionsFilter`, ported 1:1 - a substring
// match over Object.values(fn), i.e. every string or number the catalogue happened to send, around
// 25 columns. It answered with far more than anyone meant: "eol" returned nearly every report
// through cfn_preset_name, "svg" matched cfn_icon_url, "itgen" matched cfn_description (the KEY,
// not the label), and any digit matched cfn_id, cfn_sorting_order or cfrstat_cnt.
//
// It was then narrowed to the backend's own *_i18n columns, which fixed the noise but left one
// inconsistency: those are filled from the language in the GMR profile (l_user_language in
// gmr-functions.sql), while the screen renders @ngx-translate's translation of the key. Switch the
// UI to German with a Dutch profile and you searched Dutch text against German labels. Reading the
// translations directly, as here, removes that second source of truth entirely.
//
// A missing translation is deliberately NOT filtered out: instant() then returns the key, and the
// screen shows that same key, so it stays true that you can search for what you see.
//
// Lifted out of FunctionPickerComponent on 2026-08-14, unchanged, when the WebMCP `list_reports`
// tool needed the same haystack. A second definition would have re-introduced exactly the drift the
// two paragraphs above describe, one search box at a time: the picker and the agent must not be
// able to disagree about which reports the word "grootboek" finds.
//
export function buildSearchHaystack(fn: GmrFunction, translate: (key: string) => string): string
{
  let haystack = '';

  for (const field of TRANSLATED_KEY_FIELDS)
  {
    const key = fn[field];
    if (typeof key === 'string' && key.length > 0)
    {
      haystack += translate(key).toLowerCase() + '\n';
    }
  }

  for (const field of RAW_FIELDS)
  {
    const value = fn[field];
    if (typeof value === 'string' || typeof value === 'number')
    {
      haystack += String(value).toLowerCase() + '\n';
    }
  }

  return haystack;
}
